// Riportok (5.3, UI-7): how each campaign did, as the marketer made it — one
// card per campaign across its countries — and, under the cards, every
// country in one table beside the Klaviyo campaigns of the last 24 months
// (6.1), the view the Klaviyo report is held against.
//
// Every figure names its source. Postgres is the truth and always answers:
// the sends, what came back, the unsubscribes, the orders the attribution
// window credited. ClickHouse, the event warehouse, answers the opens and the
// clicks when it can; when it cannot, the figure says "nem elérhető", never 0.

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { DownloadIcon, Grid3x3Icon } from "lucide-react";
import { downloadReportCsv, fetchReportList, fetchTagDimensions } from "@/lib/api";
import type { ReportFilters, ReportRow, ReportSource } from "@/lib/api";
import { fetchGroupCards } from "@/lib/tracking";
import type { GroupCard } from "@/lib/tracking";
import { useLoad } from "@/lib/useLoad";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { DataTable } from "@/components/emc/DataTable";
import { Field } from "@/components/emc/Field";
import { PageHeader } from "@/components/emc/PageHeader";
import { SourceMark } from "@/components/emc/SourceMark";
import { StatusPill } from "@/components/emc/StatusPill";
import { Toolbar } from "@/components/emc/Toolbar";
import { Callout, EmptyState, ErrorState, LoadingState } from "@/components/emc/states";
import { addDays, utcDay } from "@/components/emc/days";
import { count, minute } from "@/components/emc/format";
import { labelOf } from "@/components/emc/status";
import type { Tone } from "@/components/emc/status";
import { audienceText, euro, figures, fxNote, permilleText, warehouseNote } from "@/pages/reports/model";
import { KNOWN_COUNTRIES } from "@/pages/contacts/model";
import type { Figure } from "@/pages/reports/model";
import { cn } from "@/lib/utils";
import { Country } from "@/components/emc/Flag";

// Every country a Klaviyo account was pulled for, HR, NL and SI included.
const COUNTRIES = ["", ...KNOWN_COUNTRIES];

// Slice 6.1: the last twenty-four months of Klaviyo sit in the same table as
// our own campaigns. A Klaviyo row is marked and does not open — there is no
// timeline or link breakdown behind it, only the figures they reported, its
// subject and who it went to. Both sources by default: until our own sends
// take over, Klaviyo's campaigns are most of what there is to look at, and
// the comparison is the point of the table.
const DEFAULT_SOURCE: ReportSource = "all";
const SOURCES: { value: ReportSource; label: string }[] = [
  { value: "own", label: "saját" },
  { value: "klaviyo", label: "Klaviyo" },
  { value: "all", label: "mind" },
];

const TONE_TEXT: Record<Tone, string> = {
  running: "text-blue-ink",
  scheduled: "text-indigo-ink",
  good: "text-green-ink",
  warn: "text-orange-ink",
  danger: "text-red-ink",
  muted: "text-foreground",
};

function reportHref(card: Pick<GroupCard, "group_id" | "campaign_id">): string {
  return card.group_id !== null ? `/riportok/csoport/${card.group_id}` : `/riportok/kampany/${card.campaign_id}`;
}

function number(value: number | null): string {
  return count(value);
}

// --- the cards --------------------------------------------------------------------------

// A card's figures, grouped under the source they came from.
const CARD_POSTGRES = ["sent", "delivered", "bounced", "complained", "unsubscribed", "revenue"];
const CARD_WAREHOUSE = ["opens", "clicks"];
const CARD_LABEL: Record<string, string> = { opens: "Nyitás", clicks: "Kattintás" };

function CardFigures({ shown }: { shown: Figure[] }) {
  return (
    <dl className="m-0 grid grid-cols-3 gap-x-4 gap-y-3">
      {shown.map((figure) => (
        <div key={figure.key} className="flex min-w-0 flex-col gap-0.5">
          <dt className="caps-label truncate">{CARD_LABEL[figure.key] ?? figure.label}</dt>
          <dd className="m-0 flex flex-col" title={figure.detail ? `${figure.comparison} · ${figure.detail}` : figure.comparison}>
            <span
              className={cn(
                "font-semibold tabular-nums",
                figure.available ? TONE_TEXT[figure.tone] : "font-normal text-muted-foreground",
              )}
            >
              {figure.key === "sent" || figure.key === "revenue" || !figure.available ? figure.value : figure.short}
            </span>
            <span className="truncate text-label text-muted-foreground">
              {figure.key === "revenue" ? figure.short : figure.key === "sent" || !figure.available ? "" : figure.value}
            </span>
            {figure.detail && <span className="truncate text-label text-muted-foreground">{figure.detail}</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function CampaignCard({ card, window }: { card: GroupCard; window: string }) {
  const all = figures(card.totals.postgres, card.totals.clickhouse, window);
  const pick = (keys: string[]) => keys.map((key) => all.find((figure) => figure.key === key)).filter((figure): figure is Figure => figure !== undefined);
  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle className="min-w-0 truncate">
          <Link to={reportHref(card)} className="text-ink hover:text-blue-ink">
            {card.name}
          </Link>
        </CardTitle>
        <CardAction>
          <StatusPill domain={card.group_id !== null ? "campaignGroup" : "campaign"} state={card.state} />
        </CardAction>
        <CardDescription>
          <span className="data-mono">{card.countries.join(" ")}</span>
          {card.started_at ? ` · indult ${minute(card.started_at)}` : " · még nem indult"}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <section className="flex flex-col gap-2">
          <SourceMark source="Postgres" />
          <CardFigures shown={pick(CARD_POSTGRES)} />
        </section>
        <section className="flex flex-col gap-2 border-t border-line pt-3">
          <SourceMark source="ClickHouse" />
          <CardFigures shown={pick(CARD_WAREHOUSE)} />
        </section>
      </CardContent>
    </Card>
  );
}

// --- the country table ------------------------------------------------------------------

/** The Klaviyo ids behind the names, on hover: two segments can share one. */
function idsOf(entries: ReportRow["audience_included"]): string {
  return (entries ?? []).map((entry) => entry.id).join(", ");
}

/** The campaign cell: its name, and on a Klaviyo row the subject and who it
 * went to — the segment's name, not its Klaviyo id. */
function CampaignName({ row }: { row: ReportRow }) {
  const included = audienceText(row.audience_included);
  const excluded = audienceText(row.audience_excluded);
  return (
    <>
      <span className="font-medium">{row.name}</span>
      {row.source !== "own" && <span className="text-muted-foreground"> · Klaviyo</span>}
      {row.subject && (
        <span className="block text-label text-muted-foreground" title={row.preheader ?? undefined}>
          {row.subject}
        </span>
      )}
      {included && (
        <span className="block text-label text-muted-foreground" title={idsOf(row.audience_included)}>
          <span className="text-ink">Közönség:</span> {included}
        </span>
      )}
      {excluded && (
        <span className="block text-label text-muted-foreground" title={idsOf(row.audience_excluded)}>
          <span className="text-ink">Kizárva:</span> {excluded}
        </span>
      )}
    </>
  );
}

const LIST_COLUMNS: ColumnDef<ReportRow>[] = [
  { id: "country", header: "Ország", cell: ({ row }) => <Country code={row.original.country} /> },
  {
    id: "name",
    header: "Kampány",
    cell: ({ row }) => <CampaignName row={row.original} />,
    // Wraps, but not to a word per line: the table scrolls sideways instead.
    meta: { wrap: true, className: "min-w-56" },
  },
  {
    id: "started",
    header: "Indult",
    cell: ({ row }) => minute(row.original.started_at),
    meta: { mono: true, muted: true },
  },
  { id: "sent", header: "Küldve", cell: ({ row }) => number(row.original.sent), meta: { align: "right" } },
  {
    id: "delivered",
    header: "Kézbesítve",
    cell: ({ row }) => permilleText(row.original.delivered_permille),
    meta: { align: "right" },
  },
  {
    id: "bounce",
    header: "Bounce",
    cell: ({ row }) => permilleText(row.original.bounce_permille),
    meta: { align: "right" },
  },
  {
    id: "complaint",
    header: "Panasz",
    cell: ({ row }) => permilleText(row.original.complaint_permille),
    meta: { align: "right" },
  },
  {
    id: "unsubscribed",
    header: "Leiratkozás",
    cell: ({ row }) => number(row.original.unsubscribed),
    meta: { align: "right" },
  },
  {
    id: "opens",
    header: "Egyedi nyitás",
    cell: ({ row }) => number(row.original.opens_total),
    meta: { align: "right" },
  },
  {
    id: "clicks",
    header: "Egyedi kattintás",
    cell: ({ row }) => number(row.original.unique_clicks),
    meta: { align: "right" },
  },
  { id: "ctr", header: "CTR", cell: ({ row }) => permilleText(row.original.ctr_permille), meta: { align: "right" } },
  { id: "orders", header: "Rendelés", cell: ({ row }) => number(row.original.orders), meta: { align: "right" } },
  {
    id: "revenue",
    header: "Bevétel",
    cell: ({ row }) => (
      <span title={fxNote(row.original.fx_first_day, row.original.fx_last_day) || undefined}>
        {euro(row.original.revenue_eur_cents)}
      </span>
    ),
    meta: { align: "right" },
  },
];

// --- the page --------------------------------------------------------------------------------

export default function Reports() {
  const navigate = useNavigate();
  const today = utcDay(new Date());
  // The last thirty days: a year of campaigns is the CSV's job, not a screen's.
  const [filters, setFilters] = useState<ReportFilters>(() => ({
    since: addDays(today, -29),
    until: today,
    source: DEFAULT_SOURCE,
  }));
  const [applied, setApplied] = useState<ReportFilters>(filters);
  const [dimensionKey, setDimensionKey] = useState("");
  const [tagValue, setTagValue] = useState("");
  const [dimensions] = useLoad(fetchTagDimensions, []);
  const [cards, reloadCards] = useLoad(
    () =>
      fetchGroupCards({
        country: applied.country,
        tag: applied.tag,
        since: applied.since,
        until: applied.until,
      }),
    [applied],
  );
  const [list, reloadList] = useLoad(() => fetchReportList(applied), [applied]);
  const [csvError, setCsvError] = useState<string | null>(null);

  function apply(next: ReportFilters = filters) {
    const tag = dimensionKey && tagValue.trim() ? `${dimensionKey}:${tagValue.trim()}` : undefined;
    const settled = { ...next, tag };
    setFilters(settled);
    setApplied(settled);
  }

  function onCsv() {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(applied)) {
      if (value) query.set(key, String(value));
    }
    const suffix = query.toString();
    setCsvError(null);
    downloadReportCsv(`/reports/campaigns.csv${suffix ? `?${suffix}` : ""}`, "kampanyok.csv").catch(
      (cause: unknown) => setCsvError(cause instanceof Error ? cause.message : "A CSV nem töltődött le."),
    );
  }

  const note = useMemo(() => (cards.data ? warehouseNote(cards.data) : null), [cards.data]);

  return (
    <>
      <PageHeader
        title="Riportok"
        description={
          <>
            Kampányonként, minden országával együtt, és alatta az összes ország egy táblában, a
            Klaviyóból áthozott kampányokkal együtt (az utolsó 24 hónap, tárggyal és közönséggel).
            A küldés, a kézbesítés, a leiratkozás és a bevétel a Postgresből jön — ez az igazság;
            a nyitás és a kattintás az eseménytárházból (ClickHouse). Minden szám mellett ott a
            forrása.
          </>
        }
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/riportok/pivot">
                <Grid3x3Icon />
                Pivot
              </Link>
            </Button>
            <Button variant="outline" type="button" onClick={onCsv}>
              <DownloadIcon />
              CSV
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-6">
        {csvError !== null && <ErrorState message={csvError} />}

        <Toolbar>
          <Field label="Ország" htmlFor="report-country">
            <NativeSelect
              id="report-country"
              value={filters.country ?? ""}
              onChange={(event) => setFilters({ ...filters, country: event.target.value || undefined })}
            >
              {COUNTRIES.map((code) => (
                <option key={code} value={code}>
                  {code === "" ? "mind" : code}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Tag" htmlFor="report-tag">
            <NativeSelect id="report-tag" value={dimensionKey} onChange={(event) => setDimensionKey(event.target.value)}>
              <option value="">mind</option>
              {(dimensions.data ?? []).map((dimension) => (
                <option key={dimension.key} value={dimension.key}>
                  {dimension.label}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Érték" htmlFor="report-tag-value">
            <Input
              id="report-tag-value"
              className="w-40"
              type="text"
              value={tagValue}
              maxLength={80}
              onChange={(event) => setTagValue(event.target.value)}
            />
          </Field>
          <Field label="Kezdet" htmlFor="report-since">
            <Input
              id="report-since"
              className="w-auto"
              type="date"
              value={filters.since ?? ""}
              onChange={(event) => setFilters({ ...filters, since: event.target.value || undefined })}
            />
          </Field>
          <Field label="Vég" htmlFor="report-until">
            <Input
              id="report-until"
              className="w-auto"
              type="date"
              value={filters.until ?? ""}
              onChange={(event) => setFilters({ ...filters, until: event.target.value || undefined })}
            />
          </Field>
          <Button type="button" onClick={() => apply()}>
            Szűrés
          </Button>
        </Toolbar>

        {note !== null && (
          <Callout tone="warn">
            {note.text}
            {note.reason && (
              <span className="mt-1 block truncate text-label text-muted-foreground" title={note.reason}>
                {note.reason}
              </span>
            )}
          </Callout>
        )}

        <section className="flex flex-col gap-3">
          <h2>Kampányok</h2>
          {cards.error !== null && <ErrorState message={cards.error} onRetry={reloadCards} />}
          {cards.data === null ? (
            cards.error === null && <LoadingState lines={4} />
          ) : cards.data.cards.length === 0 ? (
            <EmptyState title="Ebben a szűrésben nincs kampány." description="Tágítsd az időszakot, vagy vedd le a taget." />
          ) : (
            <div className="grid gap-4 xl:grid-cols-2">
              {cards.data.cards.map((card) => (
                <CampaignCard
                  key={`${card.group_id ?? "c"}-${card.campaign_id ?? card.group_id}`}
                  card={card}
                  window={cards.data?.primary_window ?? "klaviyo"}
                />
              ))}
            </div>
          )}
        </section>

        <Card>
          <CardHeader>
            <CardTitle>Országonként, egy táblában</CardTitle>
            <CardDescription>
              Egy sor egy ország kampánya — ez vethető össze a Klaviyo-riporttal. A sorra kattintva
              megnyílik a kampány riportja; a Klaviyo-sorok mögött nincs részletes riport, de ott a
              tárgyuk, és hogy melyik szegmensnek vagy listának mentek, és kit zártak ki.
            </CardDescription>
            <CardAction>
              <NativeSelect
                aria-label="Forrás"
                value={applied.source ?? DEFAULT_SOURCE}
                onChange={(event) => {
                  const next = { ...applied, source: event.target.value as ReportSource };
                  setFilters({ ...filters, source: next.source });
                  setApplied(next);
                }}
              >
                {SOURCES.map((source) => (
                  <option key={source.value} value={source.value}>
                    {source.label}
                  </option>
                ))}
              </NativeSelect>
            </CardAction>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {list.error !== null && <ErrorState message={list.error} onRetry={reloadList} />}
            <p className="m-0 flex flex-wrap items-center gap-x-3 text-label text-muted-foreground">
              <span>
                <SourceMark source="Postgres" />: küldve, kézbesítve, bounce, panasz, leiratkozás, rendelés, bevétel (
                {labelOf("attributionWindow", "klaviyo")})
              </span>
              <span>
                <SourceMark source="ClickHouse" />: egyedi nyitás, egyedi kattintás, CTR
              </span>
            </p>
            <DataTable
              label="Országonként"
              variant="plain"
              className="-mx-5.5 border-t border-separator"
              columns={LIST_COLUMNS}
              data={list.data?.rows ?? []}
              loading={list.data === null && list.error === null}
              getRowId={(row) => `${row.source}:${row.campaign_id ?? row.external_id}`}
              onRowClick={(row) => {
                if (row.campaign_id !== null) navigate(`/riportok/kampany/${row.campaign_id}`);
              }}
              rowClassName={(row) => (row.campaign_id === null ? "cursor-default" : undefined)}
              empty={<EmptyState bare title="Nincs a szűrésnek megfelelő kampány." />}
            />
            {list.data !== null && (
              <p className="m-0 text-label text-muted-foreground">{number(list.data.rows.length)} sor</p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
